<!-- This component is the Producer Number Dropdown for the Confirmation Step. -->
<template>
	<div class="col-span-2 grid grid-cols-2 gap-4 *:w-full *:max-w-none">
		<FormKit
			v-if="insuranceVendorOptions.length > 1"
			:disabled="sessionDocumentsStore.currentStep?.completed"
			v-model="proposedInsured.insuranceProducerNumber"
			:label="
				t('document.producerNumber', {
					vendorName: sessionStore.activeInsuranceCarrier,
				})
			"
			:options="insuranceVendorOptions"
			type="dropdown"
			name="insuranceProducerNumber"
			label-class="required"
			validation="required"
			:placeholder="t('document.selectNumber')"
		/>
		<FormKit
			v-if="oapVendorOptions.length > 1 && proposedInsured.oapIncluded"
			:disabled="sessionDocumentsStore.currentStep?.completed"
			v-model="proposedInsured.oapProducerNumber"
			:label="
				t('document.producerNumber', {
					vendorName: sessionStore.activeOapProvider,
				})
			"
			:options="oapVendorOptions"
			type="dropdown"
			name="oapProducerNumber"
			label-class="required"
			validation="required"
			:placeholder="t('document.selectNumber')"
		/>
	</div>
</template>

<script setup lang="ts">
import type { ProducerNumber } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import { usePresentationLocale } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { SalesContact } from '@/types'
import { FormKit } from '@formkit/vue'
import { ref, computed, watch } from 'vue'

type ProducerNumberOption = {
	label: string
	value: ProducerNumber
}

const { t } = usePresentationLocale()
const authStore = useAuthStore()
const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const insuranceVendorOptions = ref<ProducerNumberOption[]>([])
const oapVendorOptions = ref<ProducerNumberOption[]>([])

const proposedInsured = computed({
	get: () =>
		sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		) as Plan,
	set: (value) => proposedInsured.value.update(value),
})

// Set the insurance producer numbers for the proposedInsured.
const handleInsuranceSetup = (salesContact: SalesContact | undefined) => {
	const producerNumbers =
		salesContact?.producerNumbers?.data.filter(
			(producerNumber: ProducerNumber) =>
				producerNumber.vendor === sessionStore.activeInsuranceCarrier
		) ?? []

	insuranceVendorOptions.value = producerNumbers.map(
		(producerNumber: ProducerNumber) => ({
			label: producerNumber.producerNumber,
			value: {
				id: producerNumber.id,
				producerNumber: producerNumber.producerNumber,
				vendor: producerNumber.vendor,
			},
		})
	)

	if (insuranceVendorOptions.value.length === 1) {
		proposedInsured.value.insuranceProducerNumber =
			insuranceVendorOptions.value[0].value
	}
}

// Set the oap producer numbers for the proposedInsured.
const handleOapSetup = (salesContact: SalesContact | undefined) => {
	const planHolder = sessionStore.getPlanHolder(
		sessionDocumentsStore.currentPlanHolderID
	) as Plan

	if (!planHolder.oapIncluded) {
		return
	}

	const oapProducerNumbers =
		salesContact?.producerNumbers?.data.filter(
			(producerNumber: ProducerNumber) =>
				producerNumber.vendor === sessionStore.activeOapProvider
		) ?? []

	oapVendorOptions.value = oapProducerNumbers.map(
		(producerNumber: ProducerNumber) => ({
			label: producerNumber.producerNumber,
			value: {
				id: producerNumber.id,
				producerNumber: producerNumber.producerNumber,
				vendor: producerNumber.vendor,
			},
		})
	)

	if (oapVendorOptions.value.length === 1) {
		proposedInsured.value.oapProducerNumber = oapVendorOptions.value[0].value
	}
}

// Watch the proposedInsured value and set the insurance and oap producer numbers when the current plan holder is changed.
watch(
	proposedInsured,
	() => {
		const salesContact = authStore.user?.salesContact

		handleInsuranceSetup(salesContact)
		handleOapSetup(salesContact)
	},
	{ immediate: true }
)
</script>

<style scoped>
@reference "tailwindcss";
:deep(.formkit-outer:not([data-type='checkbox']) .formkit-label) {
	@apply pb-2;
}

:deep(.formkit-label.required::after) {
	content: '*';
	@apply text-red-400;
	margin-left: 6px;
}
</style>
